import { useState } from 'react';
import { BookOpen, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { VideoEditorBookendsPanel } from '@/components/VideoEditorBookendsPanel';
import {
  BOOKEND_TIMELINE_STYLES,
  activeBookendDuration,
  bookendSlotHasMedia,
  normalizeVideoBookends,
} from '@/lib/videoEditorBookends';

export function VideoEditorTimelineBookendsControl({
  bookends,
  onChange,
  onPersist,
  defaultTitle = '',
  aspectRatio = '16:9',
  narrationProjectId = null,
  storyId = null,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const norm = normalizeVideoBookends(bookends, { defaultTitle, aspectRatio });
  const introOn = norm.intro.enabled && bookendSlotHasMedia(norm.intro);
  const outroOn = norm.outro.enabled && bookendSlotHasMedia(norm.outro);
  const introDur = activeBookendDuration(norm.intro);
  const outroDur = activeBookendDuration(norm.outro);

  const enableSlot = (key) => {
    onChange((prev) => {
      const base = normalizeVideoBookends(prev, { defaultTitle, aspectRatio });
      const slot = base[key];
      return {
        ...base,
        [key]: {
          ...slot,
          enabled: true,
          title: slot.title || defaultTitle || '',
        },
      };
    });
    onPersist?.();
    setOpen(true);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`gap-1.5 h-7 ${compact ? 'text-[10px] px-2' : 'text-xs'}`}
          style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
          data-testid="timeline-bookends-btn"
        >
          <BookOpen className="w-3.5 h-3.5" />
          {compact ? 'Bookends' : 'Intro / Outro'}
          {(introOn || outroOn) ? (
            <span className="text-[9px] rounded px-1 py-0.5" style={{ background: 'var(--bg-tertiary)' }}>
              {[introOn && `in ${introDur}s`, outroOn && `out ${outroDur}s`].filter(Boolean).join(' · ')}
            </span>
          ) : null}
          <ChevronDown className="w-3 h-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(420px,92vw)] max-h-[min(70vh,560px)] overflow-y-auto p-3"
        align="start"
        side="bottom"
      >
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            className="text-[10px] font-semibold rounded-lg px-2.5 py-1.5 border-2"
            style={{
              borderColor: BOOKEND_TIMELINE_STYLES.intro.border,
              background: BOOKEND_TIMELINE_STYLES.intro.bg,
              color: BOOKEND_TIMELINE_STYLES.intro.text,
            }}
            onClick={() => enableSlot('intro')}
            data-testid="timeline-add-intro-bookend"
          >
            + Intro bookend
          </button>
          <button
            type="button"
            className="text-[10px] font-semibold rounded-lg px-2.5 py-1.5 border-2"
            style={{
              borderColor: BOOKEND_TIMELINE_STYLES.outro.border,
              background: BOOKEND_TIMELINE_STYLES.outro.bg,
              color: BOOKEND_TIMELINE_STYLES.outro.text,
            }}
            onClick={() => enableSlot('outro')}
            data-testid="timeline-add-outro-bookend"
          >
            + Outro bookend
          </button>
        </div>
        <p className="text-[11px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
          Intro/outro cards appear at the start and end of the timeline (violet intro, teal outro).
          Background music starts from the intro when one is added.
        </p>
        <VideoEditorBookendsPanel
          bookends={norm}
          defaultTitle={defaultTitle}
          aspectRatio={aspectRatio}
          narrationProjectId={narrationProjectId}
          storyId={storyId}
          onChange={onChange}
          onPersist={onPersist}
        />
      </PopoverContent>
    </Popover>
  );
}
